import { Linkedin, ArrowUpRight } from "lucide-react";

function Avatar({ initials, testid, image, name }) {
  if (image) {
    return (
      <div
        data-testid={testid}
        className="relative aspect-square w-full bg-secondary border border-border overflow-hidden"
      >
        <img
          src={image}
          alt={name || "Portrait"}
          className="absolute inset-0 w-full h-full object-cover grayscale contrast-[1.05] transition-transform duration-700 ease-out hover:scale-[1.03]"
        />
      </div>
    );
  }
  return (
    <div
      data-testid={testid}
      className="relative aspect-square w-full bg-secondary border border-border overflow-hidden flex items-end justify-start"
    >
      <span className="font-display font-medium leading-none tracking-tighter text-[28vw] md:text-[16rem] -mb-6 md:-mb-10 -ml-4 md:-ml-6 select-none">
        {initials}
      </span>
      <span className="absolute top-4 right-4 font-mono-label text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
        Portrait · TBD
      </span>
    </div>
  );
}

const advisors = [
  {
    name: "Jason Glickman",
    role: "Advisor",
    initials: "JG",
    linkedin: "https://www.linkedin.com/in/jasonglickman/",
    testid: "advisor-jason-glickman",
    image: "https://customer-assets.emergentagent.com/job_distrib-tech/artifacts/qempy97k_1750870849046.jpg",
  },
  {
    name: "Joshua Feuer",
    role: "Advisor",
    initials: "JF",
    linkedin: "https://www.linkedin.com/in/joshuafeuer/",
    testid: "advisor-joshua-feuer",
    image: "https://customer-assets.emergentagent.com/job_distrib-tech/artifacts/2h8569xn_7467aaba-6ea9-460a-be74-270239a76409_medium.jpg",
  },
];

export default function Team() {
  return (
    <div data-testid="page-team">
      {/* INTRO */}
      <section className="container-x pt-12 md:pt-20 pb-16">
        <p className="eyebrow mb-8">Team</p>
        <h1
          className="hero-headline text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] max-w-[16ch]"
          data-testid="team-headline"
        >
          Operators who have scaled, sold and rebuilt.
        </h1>
      </section>

      {/* FOUNDER */}
      <section className="border-t border-border">
        <div className="container-x py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Avatar
              initials="AR"
              testid="founder-avatar"
              image="https://customer-assets.emergentagent.com/job_distrib-tech/artifacts/7cvjuxl6_1755518884850.jpg"
              name="Andrew S. Reis"
            />
            <div className="mt-6 flex items-center justify-between">
              <div>
                <p className="font-display text-2xl tracking-tight" data-testid="founder-name">
                  Andrew S. Reis
                </p>
                <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-muted-foreground mt-2">
                  Founder &amp; CEO
                </p>
              </div>
              <a
                href="https://www.linkedin.com/in/andrewsreis/"
                target="_blank"
                rel="noreferrer"
                data-testid="founder-linkedin"
                className="inline-flex items-center gap-2 border border-foreground px-3 py-2 font-mono-label text-[10px] tracking-[0.22em] uppercase hover:bg-foreground hover:text-background transition-colors"
              >
                <Linkedin size={12} /> LinkedIn
              </a>
            </div>
          </div>

          <div className="md:col-span-7 text-base md:text-lg font-light leading-relaxed text-foreground/85 space-y-5">
            <p>
              Andrew S. Reis is an entrepreneur, operator, investor and business strategist with more than two decades of experience building, scaling, financing and commercializing companies across advertising technology, media, commerce and emerging digital ecosystems.
            </p>
            <p>
              Known for identifying market shifts early and executing aggressively against them, Andrew has spent his career operating at the intersection of audience, media, technology, monetization and distribution.
            </p>
            <p>
              He was Co-Founder and COO of <span className="font-medium text-foreground">Tremor Video</span>, helping scale the business into one of the largest independent video advertising technology companies in the market and a successful NYSE public offering. He was an early investor and executive board member in <span className="font-medium text-foreground">Elite Daily</span>, guiding the company through its hyper-growth years prior to acquisition, and later played a significant leadership role in the transformation and turnaround of <span className="font-medium text-foreground">Genesis Media</span>.
            </p>
            <p>
              Today, through ventures including <span className="font-medium text-foreground">Sellrly</span> and <span className="font-medium text-foreground">GetLive</span>, Andrew works across social commerce, creator monetization, live shopping, retail media, audience intelligence, closed-loop attribution, performance advertising, marketplace distribution and connected TV commerce — partnering with platforms such as TikTok Shop, Whatnot, Amazon Live, Shopify, eBay Live and Palmstreet.
            </p>
            <p>
              His leadership style blends entrepreneurial speed with practical commercial discipline: deeply hands-on, highly collaborative, and intensely focused on measurable business outcomes rather than vanity metrics.
            </p>
          </div>
        </div>
      </section>

      {/* ADVISORS */}
      <section className="border-t border-border bg-secondary/40">
        <div className="container-x py-20 md:py-28">
          <div className="mb-12">
            <p className="eyebrow mb-4">Advisors</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight max-w-[20ch]">
              Strategic counsel from senior operators.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {advisors.map((a) => (
              <div key={a.name} data-testid={a.testid}>
                <Avatar
                  initials={a.initials}
                  testid={`${a.testid}-avatar`}
                  image={a.image}
                  name={a.name}
                />
                <div className="mt-6 flex items-end justify-between">
                  <div>
                    <p className="font-display text-2xl tracking-tight">{a.name}</p>
                    <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-muted-foreground mt-2">
                      {a.role}
                    </p>
                  </div>
                  <a
                    href={a.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${a.testid}-linkedin`}
                    className="inline-flex items-center gap-2 border border-foreground px-3 py-2 font-mono-label text-[10px] tracking-[0.22em] uppercase hover:bg-foreground hover:text-background transition-colors"
                  >
                    LinkedIn <ArrowUpRight size={12} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
